import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ModalTableEnhancer from './components/ModalTableEnhancer';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import ProductMaster from './pages/ProductMaster';
import EquipmentMaster from './pages/EquipmentMaster';
import Equipment from './pages/Equipment';
import EquipmentTrain from './pages/EquipmentTrain';
import CleaningConfig from './pages/CleaningConfig';
import AnalyticalMethods from './pages/AnalyticalMethods';
import VRL from './pages/VRL';
import WorstCase from './pages/WorstCase';
import CleaningValidationSettings from './pages/CleaningValidationSettings';
import GapAssessment from './pages/GapAssessment';
import MACO from './pages/MACO';
import SamplingAndAcceptance from './pages/SamplingAndAcceptance';
import Results from './pages/Results';
import CVReport from './pages/CVReport';
import Training from './pages/Training';
import Documents from './pages/Documents';
import CCV from './pages/CCV';
import Revalidation from './pages/Revalidation';
import AuditTrail from './pages/AuditTrail';
import Users from './pages/Users';
import AppConfiguration from './pages/AppConfiguration';
import CommercialSetup from './pages/CommercialSetup';
import IntegrationHub from './pages/IntegrationHub';
import RiskAssessment from './pages/RiskAssessment';
import ChangeControl from './pages/ChangeControl';
import ProductOverview from './pages/ProductOverview';
import ApiMaster from './pages/ApiMaster';
import AIAssistant from './pages/AIAssistant';
import Layout from './components/Layout';
import { ThemeProvider } from './context/ThemeContext';
import { authAPI } from './services/api';
import { AppSettingsProvider } from './context/AppSettingsContext';

import ForgotPassword from './pages/ForgotPassword';
import LicenseLocked from './pages/LicenseLocked';
import AccessLocked from './pages/AccessLocked';
import ResetPassword from './pages/ResetPassword';

const SESSION_TIMEOUT_MINUTES = Number(process.env.REACT_APP_SESSION_TIMEOUT_MINUTES || 15);
const SESSION_TIMEOUT_MS = SESSION_TIMEOUT_MINUTES * 60 * 1000;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(localStorage.getItem('token') && localStorage.getItem('user')));
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      return null;
    }
  });
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false);
  const warningTimerRef = React.useRef(null);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('lastActivityAt', String(Date.now()));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = useCallback(() => {
    authAPI.endFileSession().catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivityAt');
    setSessionWarningOpen(false);
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  // Covers fresh sign-ins and sessions restored from a previous visit.
  useEffect(() => {
    if (isAuthenticated) authAPI.startFileSession().catch(() => {});
  }, [isAuthenticated]);

  const handlePasswordChanged = useCallback(() => {
    handleLogout();
  }, [handleLogout]);

  const updateActivity = useCallback(() => {
    localStorage.setItem('lastActivityAt', String(Date.now()));
    setSessionWarningOpen(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setSessionWarningOpen(false);
      if (warningTimerRef.current) {
        window.clearTimeout(warningTimerRef.current);
      }
      return undefined;
    }

    updateActivity();
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, updateActivity, { passive: true }));

    const timer = window.setInterval(() => {
      const last = Number(localStorage.getItem('lastActivityAt') || Date.now());
      const idleMs = Date.now() - last;

      if (idleMs > SESSION_TIMEOUT_MS) {
        toast.info(`Session timed out after ${SESSION_TIMEOUT_MINUTES} minutes of inactivity.`);
        handleLogout();
        return;
      }

      if (idleMs > SESSION_TIMEOUT_MS - 120000 && !sessionWarningOpen) {
        setSessionWarningOpen(true);
        toast.warning('Your session will expire in less than 2 minutes. Move the mouse or press any key to stay signed in.');
      }
    }, 15000);

    return () => {
      window.clearInterval(timer);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, updateActivity));
      if (warningTimerRef.current) {
        window.clearTimeout(warningTimerRef.current);
      }
    };
  }, [isAuthenticated, handleLogout, sessionWarningOpen, updateActivity]);

  const staySignedIn = useCallback(() => {
    updateActivity();
    setSessionWarningOpen(false);
  }, [updateActivity]);

  const mustChangePassword = Boolean(user?.force_password_change);

  return (
    <AppSettingsProvider>
    <ThemeProvider>
    <Router>
      <ModalTableEnhancer />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {sessionWarningOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', boxShadow: 'var(--shadow-lg)', width: 'min(420px, calc(100vw - 24px))', padding: '24px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: 'var(--fs-xl)' }}>Session expiring soon</h3>
            <p style={{ margin: '0 0 20px', color: 'var(--text-secondary)' }}>
              This session will time out due to inactivity in less than 2 minutes. Keep working to stay signed in.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn btn-outline" onClick={() => setSessionWarningOpen(false)}>
                Dismiss
              </button>
              <button type="button" className="btn btn-primary" onClick={staySignedIn}>
                Stay signed in
              </button>
            </div>
          </div>
        </div>
      )}

      <Routes>
        <Route 
          path="/login" 
          element={
            isAuthenticated ? (
              mustChangePassword ? <Navigate to="/change-password" /> : <Navigate to="/dashboard" />
            ) : (
            <Login onLogin={handleLogin} />
            )
          } 
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/license-locked" element={<LicenseLocked />} />
        <Route path="/access-locked" element={<AccessLocked />} />

        {isAuthenticated && mustChangePassword && (
          <Route
            path="/change-password"
            element={<ChangePassword onPasswordChanged={handlePasswordChanged} onLogoutForced={handleLogout} />}
          />
        )}
        
        {isAuthenticated ? (
          mustChangePassword ? (
            <Route path="*" element={<Navigate to="/change-password" />} />
          ) : (
          <Route element={<Layout user={user} onLogout={handleLogout} />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/product-master" element={<ProductMaster />} />
            <Route path="/product-master/:productId/overview" element={<ProductOverview />} />
            <Route path="/products" element={<Navigate to="/product-master" replace />} />
            <Route path="/api-master" element={<ApiMaster />} />
            <Route path="/equipment" element={<Equipment />} />
            <Route path="/equipment-master" element={<EquipmentMaster />} />
            <Route path="/equipment-trains" element={<EquipmentTrain />} />
            <Route path="/cleaning-config" element={<CleaningConfig />} />
            <Route path="/analytical-methods" element={<AnalyticalMethods />} />
            <Route path="/vrl" element={<VRL />} />
            <Route path="/worst-case" element={<WorstCase />} />
            <Route path="/cleaning-validation-settings" element={<CleaningValidationSettings />} />
            <Route path="/gap-assessment" element={<GapAssessment />} />
            <Route path="/maco" element={<MACO />} />
            <Route path="/acceptance-criteria" element={<SamplingAndAcceptance />} />
            <Route path="/sampling-plans" element={<Navigate to="/acceptance-criteria" replace />} />
            <Route path="/results" element={<Results />} />
            <Route path="/study-report" element={<CVReport />} />
            <Route path="/study-report/:planId" element={<CVReport />} />
            <Route path="/training" element={<Training />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/ccv" element={<CCV />} />
            <Route path="/revalidation" element={<Revalidation />} />
            <Route path="/audit-trail" element={<AuditTrail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/settings" element={<AppConfiguration />} />
            <Route path="/admin-settings" element={<Navigate to="/settings?tab=general" replace />} />
            <Route path="/commercial-setup" element={<CommercialSetup />} />
            <Route path="/integrations" element={<IntegrationHub />} />
            <Route path="/risk-assessment" element={<RiskAssessment />} />
            <Route path="/change-control" element={<ChangeControl />} />
            <Route path="/ai-assistant" element={<AIAssistant />} />
            <Route path="/ai-providers" element={<Navigate to="/settings?tab=ai" replace />} />
            <Route path="/alerts-notifications" element={<Navigate to="/settings?tab=alerts" replace />} />
            <Route path="/approval-flow" element={<Navigate to="/settings?tab=approval" replace />} />
            <Route path="/backup-restore" element={<Navigate to="/settings?tab=backup" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Route>
          )
        ) : (
          <Route path="*" element={<Navigate to="/login" />} />
        )}
      </Routes>
    </Router>
    </ThemeProvider>
    </AppSettingsProvider>
  );
}

export default App;
